import type { MessageDescriptor } from '@lingui/core'
import { msg } from '@lingui/core/macro'
import { useLingui } from '@lingui/react'
import { customerHooks } from '$src/lib/trpcHooks'
import {
  Group,
  NavLink,
  NativeSelect,
  Stack,
  Text,
  UnstyledButton,
} from '@mantine/core'
import { IconCalendarEvent, IconHome } from '@tabler/icons-react'
import { useNavigate } from '@tanstack/react-router'
import styles from '../BottomTabNavigator.module.css'

export type CompanyPortalTab = 'properties' | 'visits'

interface CompanyPortalNavItem {
  id: CompanyPortalTab
  icon: React.ReactNode
  label: MessageDescriptor
  route: string
}

function getCompanyPortalNavItems(
  companyId: string,
  iconSize: number,
): CompanyPortalNavItem[] {
  return [
    {
      id: 'properties',
      icon: <IconHome size={iconSize} />,
      label: msg`Properties`,
      route: `/company-portal/${companyId}`,
    },
    {
      id: 'visits',
      icon: <IconCalendarEvent size={iconSize} />,
      label: msg`Visits`,
      route: `/company-portal/${companyId}/visits`,
    },
  ]
}

/** Determine active tab from current pathname */
export function getCompanyPortalActiveTab(pathname: string): CompanyPortalTab {
  const segments = pathname.split('/')
  if (segments[3] === 'visits') return 'visits'
  return 'properties'
}

/** Extract companyId from company portal pathname, or null if not in a company context */
export function getCompanyIdFromPath(pathname: string): string | null {
  const segments = pathname.split('/')
  return segments[2] || null
}

/** Whether to show bottom nav (only on company-level pages, not detail pages) */
export function isCompanyPortalMainRoute(pathname: string): boolean {
  const segments = pathname.split('/').filter(Boolean)
  // company-portal/$companyId → 2 segments (properties main)
  // company-portal/$companyId/visits → 3 segments (visits main)
  return segments.length <= 3
}

/** Desktop sidebar navigation for company portal */
export function CompanyPortalSidebar({
  companyId,
  activeTab,
}: {
  companyId: string
  activeTab: CompanyPortalTab
}) {
  const { i18n } = useLingui()
  const navigate = useNavigate()
  const items = getCompanyPortalNavItems(companyId, 20)

  const { data: customersData } = customerHooks.useList()
  const companies = (customersData?.data || []).filter(
    (c) => c.type === 'company',
  )

  return (
    <Stack gap="md">
      {companies.length > 1 ? (
        <NativeSelect
          value={companyId}
          onChange={(e) =>
            navigate({ to: `/company-portal/${e.target.value}` })
          }
          data={companies.map((c) => ({
            value: String(c.id),
            label: c.name || String(c.id),
          }))}
          fw={600}
        />
      ) : (
        <Text fw={600} size="lg" px="xs" lineClamp={2}>
          {companies.find((c) => c.id === Number(companyId))?.name}
        </Text>
      )}

      <Stack gap={4}>
        {items.map((item) => (
          <NavLink
            key={item.id}
            label={i18n._(item.label)}
            leftSection={item.icon}
            active={activeTab === item.id}
            onClick={() => navigate({ to: item.route })}
          />
        ))}
      </Stack>
    </Stack>
  )
}

/** Mobile bottom tab navigation for company portal */
export function CompanyPortalBottomNav({
  companyId,
  activeTab,
}: {
  companyId: string
  activeTab: CompanyPortalTab
}) {
  const { i18n } = useLingui()
  const navigate = useNavigate()
  const items = getCompanyPortalNavItems(companyId, 24)

  return (
    <Group gap={0} className={styles.bottomNav}>
      {items.map((tab) => (
        <UnstyledButton
          key={tab.id}
          onClick={() => navigate({ to: tab.route })}
          className={styles.tabButton}
          data-active={activeTab === tab.id}
        >
          <Stack align="center" gap={4}>
            <div className={styles.tabIcon} data-active={activeTab === tab.id}>
              {tab.icon}
            </div>
            <Text
              size="xs"
              c={activeTab === tab.id ? 'brandOrange.6' : 'dimmed'}
              fw={activeTab === tab.id ? 600 : 400}
            >
              {i18n._(tab.label)}
            </Text>
          </Stack>
        </UnstyledButton>
      ))}
    </Group>
  )
}
